import { InboundBookingProduct } from "@deliverr/inbound-client";
import { productClient } from "Clients";
import { SPThunkAction } from "common/ReduxUtils";
import { logError, logStart } from "Logger";
import { getStorageInboundCreate } from "storage/inbounds/create/store/selector/getStorageInboundCreate";
import { selectBookingProducts } from "../selectors";
import { setBookingProducts } from "./simpleUpdateActions";
import { ProductPrepCategory } from "@deliverr/commons-clients";

interface PackageItem {
  dsku: string;
  qty: number;
}

interface PackageData {
  numberOfPkgs: number;
  items: PackageItem[];
}

export const getBookingProducts =
  (
    isDtcNetworkBooking: boolean,
    mixedSkuProductDskus?: string[],
    packages?: PackageData[]
  ): SPThunkAction<Promise<InboundBookingProduct[]>> =>
  async (dispatch, getState) => {
    const state = getState();
    const { selectedProducts, selectedProductsCaseDimensions, selectedProductsCasePacks } =
      getStorageInboundCreate(state);
    const savedBookingProducts = selectBookingProducts(state);
    // Calculate product quantities from packages if provided, otherwise fall back to selectedProducts
    const productQuantities: Record<string, number> = {};
    let dskus: string[] = [];

    if (packages && packages.length > 0) {
      // Calculate quantities from packages (more accurate)
      packages.forEach((pkg) => {
        const { numberOfPkgs, items } = pkg;
        items.forEach((item) => {
          const { dsku, qty } = item;
          productQuantities[dsku] = (productQuantities[dsku] || 0) + numberOfPkgs * qty;
        });
      });
      dskus = Object.keys(productQuantities);
    } else {
      // Fall back to selectedProducts for backward compatibility
      dskus = Object.keys(selectedProducts);
      Object.values(selectedProducts).forEach(({ dsku, qty }) => {
        productQuantities[dsku] = qty;
      });
    }

    // need to check if the value was set by the AirCargoModal, which collects box dims without products;
    const hasAirCargoInfo = savedBookingProducts.length && savedBookingProducts[0].dsku === undefined;

    if (!dskus.length) {
      if (!hasAirCargoInfo) {
        dispatch(setBookingProducts([]));
      }
      return [];
    }

    const ctx = logStart({ fn: "useFetchProducts", dskus });
    try {
      const productData = await productClient.getProducts(dskus, {
        includeCustomsInformation: true,
        includeProductPreparation: true,
      });

      const bookingProducts = dskus
        .filter((dsku) => productData?.[dsku])
        .map((dsku) => {
          const qty = productQuantities[dsku];
          const selectedProduct = selectedProducts[dsku];
          const caseQty = selectedProduct?.caseQty || 0;
          const casePackDsku = selectedProductsCasePacks?.[dsku]?.dsku;
          const { customsInformation, brandedPackaging, productPreparation } = productData![dsku];
          const caseDimensions = selectedProductsCaseDimensions[dsku];
          return {
            dsku: isDtcNetworkBooking ? dsku : casePackDsku,
            categoryName: brandedPackaging ? ProductPrepCategory.OTHER : productPreparation?.packagingType,
            countryOfOrigin: customsInformation?.originCountry,
            sixDigitHsCode: customsInformation?.tariffCode,
            qty,
            caseHeight: caseDimensions?.height,
            caseWidth: caseDimensions?.width,
            caseLength: caseDimensions?.length,
            caseWeight: caseDimensions?.weight,
            caseQty,
          };
        });

      if (mixedSkuProductDskus) {
        const filteredBookingProducts = bookingProducts.filter((bookingProduct) =>
          mixedSkuProductDskus.includes(bookingProduct.dsku)
        );
        dispatch(setBookingProducts(filteredBookingProducts));
        return filteredBookingProducts;
      } else {
        dispatch(setBookingProducts(bookingProducts));
        return bookingProducts;
      }
    } catch (err) {
      logError(ctx, err, "error fetching products");
      throw err;
    }
  };
